'use client';

import { createContext } from 'react';

type NavLink = { label: string; href: string };

export type MarketingNavLinksSeed = {
  navLinks: NavLink[];
  footerLinks: NavLink[];
};

/**
 * Seed for the marketing nav/footer links rendered on the first paint.
 *
 * The root layout starts `api.marketing.queries.listEnabledNavLinks` on the
 * server and provides the pending promise (resolving to null on failure)
 * without awaiting it. `useMarketingNavLinks()` resolves it with `use()`, so
 * only marketing surfaces wait on it: SSR and the first client render show
 * the same list, and workspace routes never block on a marketing query. The
 * promise stays in the retained root layout, so a later client navigation
 * from an app route to a marketing page still gets the seed. Without it, the
 * hook's `useQuery` is `undefined` on the first render, the hook falls back to
 * hardcoded defaults, and the nav visibly pops/reshuffles once Convex resolves.
 */
export const MarketingNavLinksContext =
  createContext<Promise<MarketingNavLinksSeed | null> | null>(null);

export function MarketingNavLinksProvider({
  value,
  children,
}: {
  value: Promise<MarketingNavLinksSeed | null> | null;
  children: React.ReactNode;
}) {
  return (
    <MarketingNavLinksContext.Provider value={value}>
      {children}
    </MarketingNavLinksContext.Provider>
  );
}
